import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaMinus, FaPlus, FaTimes } from "react-icons/fa";
import { BiImage } from "react-icons/bi";
import config from "../../config";
import carDiagram from "../../assets/images/carnew.jpg";
import Car_img2 from "../../assets/images/car_damage.png";
import ReportPhotoViewer from "../../dealers/pages/Car Report/ReportPhotoViewer";
import "../../dealers/pages/Car Report/style.css";

const MAP_WIDTH = 553;
const MAP_HEIGHT = 406;

const MARKER_COLORS = {
  P: { bg: "#0284c7", ring: "rgba(2, 132, 199, 0.45)" },
  D1: { bg: "#d97706", ring: "rgba(217, 119, 6, 0.5)" },
  D2: { bg: "#ea580c", ring: "rgba(234, 88, 12, 0.5)" },
  D3: { bg: "#dc2626", ring: "rgba(220, 38, 38, 0.55)" },
  A: { bg: "#b91c1c", ring: "rgba(185, 28, 28, 0.55)" },
  S: { bg: "#7c3aed", ring: "rgba(124, 58, 237, 0.5)" },
  W: { bg: "#475569", ring: "rgba(71, 85, 105, 0.45)" },
  R: { bg: "#2563eb", ring: "rgba(37, 99, 235, 0.5)" },
};

export function unwrapReportList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter((item) => item && typeof item === "object");
  }
  if (typeof raw !== "object") return [];
  if (Array.isArray(raw.data)) return unwrapReportList(raw.data);
  if (Array.isArray(raw.results)) return unwrapReportList(raw.results);
  if (Array.isArray(raw.points)) return unwrapReportList(raw.points);
  return [];
}

function markerKey(value) {
  return String(value || "").trim().toUpperCase();
}

function markerColor(symbol) {
  return MARKER_COLORS[markerKey(symbol)] || MARKER_COLORS.R;
}

function mapOffset(value, total) {
  if (value == null || value === "") return "0%";
  const str = String(value).trim();
  if (str.endsWith("%")) return str;
  const n = parseFloat(str);
  if (!Number.isFinite(n) || !total) return "0%";
  return `${(n / total) * 100}%`;
}

function pickPointPhoto(item) {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  const photo =
    item.photo_url ||
    item.photoUrl ||
    item.image ||
    item.imgs ||
    item.img ||
    item.src ||
    "";
  return typeof photo === "string" ? photo.trim() : "";
}

function formatPointDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function scoreTone(score) {
  const n = Number(score) || 0;
  if (n >= 90) return { key: "excellent", label: "Excellent", color: "#059669" };
  if (n >= 75) return { key: "good", label: "Good", color: "#2563eb" };
  if (n >= 60) return { key: "fair", label: "Fair", color: "#d97706" };
  return { key: "poor", label: "Needs work", color: "#dc2626" };
}

const DAMAGE_MARKER_WEIGHTS = {
  P: 1,
  D1: 2,
  D2: 3,
  D3: 4,
  A: 5,
  S: 4,
  W: 3,
  R: 2,
};

function roundScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10) / 10;
}

function questionPenalty(question) {
  const selected = question?.selected_inspection_question?.[0];
  const raw = selected?.damage ?? question?.count ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function sectionScoreFromFormula(section) {
  const questions = Array.isArray(section?.inspection_title)
    ? section.inspection_title
    : [];
  const penalty = questions.reduce(
    (sum, question) => sum + questionPenalty(question),
    0
  );
  return {
    title: section?.title || "Section",
    questions: questions.length,
    score: roundScore(Math.max(0, Math.min(100, 100 - penalty))),
  };
}

function markerWeight(point) {
  const key = markerKey(point?.symbol);
  if (Object.prototype.hasOwnProperty.call(DAMAGE_MARKER_WEIGHTS, key)) {
    return DAMAGE_MARKER_WEIGHTS[key];
  }
  return 2;
}

function damageMapScore(points) {
  const list = Array.isArray(points) ? points : [];
  const penalty = list.reduce((sum, point) => sum + markerWeight(point), 0);
  return {
    title: "Damage map",
    questions: list.length,
    score: roundScore(Math.max(0, Math.min(100, 100 - penalty))),
  };
}

export function overallFromSections(sections, points) {
  const parts = (Array.isArray(sections) ? sections : []).map(
    sectionScoreFromFormula
  );
  const mapPart = damageMapScore(points);
  if (mapPart.questions > 0) parts.push(mapPart);
  if (!parts.length) return { score: 0, parts, mapPart };
  return {
    score: roundScore(
      parts.reduce((sum, part) => sum + part.score, 0) / parts.length
    ),
    parts,
    mapPart,
  };
}

function buildLegend(apiTypes, points) {
  const bySymbol = new Map();
  (Array.isArray(apiTypes) ? apiTypes : []).forEach((item) => {
    const symbol = markerKey(item?.symbol);
    if (!symbol) return;
    bySymbol.set(symbol, {
      symbol,
      title: item.title || symbol,
      count: 0,
    });
  });
  (Array.isArray(points) ? points : []).forEach((item) => {
    const symbol = markerKey(item?.symbol);
    if (!symbol) return;
    const current = bySymbol.get(symbol) || {
      symbol,
      title: item.title || symbol,
      count: 0,
    };
    current.count += 1;
    if (item.title && current.title === symbol) current.title = item.title;
    bySymbol.set(symbol, current);
  });
  return [...bySymbol.values()];
}

function galleryItemsFromPhotos(photos) {
  return (Array.isArray(photos) ? photos : [])
    .map((item, index) => {
      if (!item) return null;
      if (typeof item === "string") {
        return {
          src: item,
          label: "Inspection photo",
          symbol: String(index + 1),
        };
      }
      const src = pickPointPhoto(item);
      if (!src) return null;
      return {
        src,
        label: item.title || item.label || "Inspection photo",
        symbol: item.symbol || String(index + 1),
      };
    })
    .filter(Boolean);
}

export default function InspectionReportBody({
  sections = [],
  damagePoints = [],
  resetKey,
}) {
  const [openSections, setOpenSections] = useState(null);
  const [activePoint, setActivePoint] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [legendTypes, setLegendTypes] = useState([]);
  const [localPoints, setLocalPoints] = useState([]);
  const [gallery, setGallery] = useState({ open: false, items: [], index: 0 });
  const pointInfoRef = useRef(null);
  const points = damagePoints.length ? damagePoints : localPoints;

  const isSectionOpen = (index) =>
    openSections == null ? true : openSections.has(index);

  const toggleSection = (index) => {
    setOpenSections((prev) => {
      const current =
        prev == null
          ? new Set(sections.map((_, i) => i))
          : new Set(prev);
      if (current.has(index)) current.delete(index);
      else current.add(index);
      return current;
    });
  };

  const openGallery = useCallback((items, start = 0) => {
    const clean = (items || []).filter((item) => item && item.src);
    if (!clean.length) return;
    setGallery({
      open: true,
      items: clean,
      index: Math.max(0, Math.min(start, clean.length - 1)),
    });
  }, []);

  const closeGallery = useCallback(() => {
    setGallery({ open: false, items: [], index: 0 });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.baseUrl}api/show_legend_car/`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        setLegendTypes(unwrapReportList(data?.data ?? data));
      })
      .catch(() => {
        if (!cancelled) setLegendTypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (resetKey == null || resetKey === "") {
      setLocalPoints([]);
      return undefined;
    }
    let cancelled = false;
    fetch(`${config.baseUrl}api/track/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: resetKey }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        setLocalPoints(unwrapReportList(data?.data ?? data));
      })
      .catch(() => {
        if (!cancelled) setLocalPoints([]);
      });
    return () => {
      cancelled = true;
    };
  }, [resetKey]);

  useEffect(() => {
    setActivePoint(null);
    setActiveIndex(-1);
    setOpenSections(null);
  }, [resetKey]);

  useEffect(() => {
    if (!activePoint || !pointInfoRef.current) return;
    pointInfoRef.current.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activePoint]);

  const legendEntries = useMemo(
    () => buildLegend(legendTypes, points),
    [legendTypes, points]
  );
  const scoreBreakdown = useMemo(
    () => overallFromSections(sections, points),
    [sections, points]
  );
  const overallTone = scoreTone(scoreBreakdown.score);
  const mapTone = scoreTone(scoreBreakdown.mapPart.score);
  const activePhoto = pickPointPhoto(activePoint);
  const damagePhotos = useMemo(
    () =>
      points
        .map((item) => {
          const src = pickPointPhoto(item);
          if (!src) return null;
          return {
            src,
            symbol: item.symbol || "",
            label: item.title || "Damage photo",
          };
        })
        .filter(Boolean),
    [points]
  );

  const selectPoint = (item, index) => {
    if (activeIndex === index) {
      setActiveIndex(-1);
      setActivePoint(null);
      return;
    }
    setActiveIndex(index);
    setActivePoint(item);
  };

  const openDamageGallery = (startSrc) => {
    const photos = points
      .map((item) => {
        const src = pickPointPhoto(item);
        return src
          ? {
              src,
              symbol: item.symbol || "",
              label: item.title || "Damage photo",
            }
          : null;
      })
      .filter(Boolean);
    const start = photos.findIndex((photo) => photo.src === startSrc);
    openGallery(photos, start < 0 ? 0 : start);
  };

  return (
    <div className="dd-report pi-report-body">
      <section className="dd-report__card dd-report__score">
        <div className="dd-report__map-heading">
          <div>
            <h3 className="dd-report__card-title">Overall inspection score</h3>
            <p className="dd-report__card-hint">
              This lot scores {scoreBreakdown.score}% overall
              {points.length ? " across inspection sections and the damage map" : ""}.
            </p>
          </div>
          <div className="dd-report__map-stats">
            <div className={`dd-report__map-stat dd-report__pct--${overallTone.key}`}>
              <strong>{scoreBreakdown.score}%</strong>
              <span>Overall</span>
            </div>
            {points.length ? (
              <div className={`dd-report__map-stat dd-report__pct--${mapTone.key}`}>
                <strong>{scoreBreakdown.mapPart.score}%</strong>
                <span>Damage map</span>
              </div>
            ) : null}
            {points.length ? (
              <div className="dd-report__map-stat">
                <strong>{points.length}</strong>
                <span>{points.length === 1 ? "Point" : "Points"}</span>
              </div>
            ) : null}
          </div>
        </div>
        {scoreBreakdown.parts.length ? (
          <div className="dd-report__score-tiles">
            {scoreBreakdown.parts.map((item, index) => {
              const tone = scoreTone(item.score);
              return (
                <div
                  className={`dd-report__tile dd-report__tile--${tone.key}`}
                  key={`${item.title}-${index}`}
                >
                  <div className="dd-report__tile-head">
                    <span>{item.title}</span>
                    <strong>{item.score}%</strong>
                  </div>
                  <div className="dd-report__tile-track">
                    <span
                      style={{
                        width: `${item.score}%`,
                        background: tone.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="dd-report__card">
          <div className="dd-report__map-heading">
            <div>
              <h3 className="dd-report__card-title">Damage map</h3>
              <p className="dd-report__card-hint">
                {points.length
                  ? "Select a marker to see the damage type, date, and photo. Click the same marker or the map to unselect."
                  : "The inspection diagram is shown below. Markers appear after damage points are recorded for this lot."}
              </p>
            </div>
            <div className="dd-report__map-stats">
              <div className={`dd-report__map-stat dd-report__pct--${mapTone.key}`}>
                <strong>{scoreBreakdown.mapPart.score}%</strong>
                <span>Map score</span>
              </div>
              {points.length ? (
                <div className="dd-report__map-stat">
                  <strong>{points.length}</strong>
                  <span>{points.length === 1 ? "Point" : "Points"}</span>
                </div>
              ) : null}
            </div>
          </div>
          {activePoint ? (
            <div className="dd-report__point-info" ref={pointInfoRef}>
              <span
                className="dd-report__point-badge"
                style={{ background: markerColor(activePoint.symbol).bg }}
              >
                {activePoint.symbol || "•"}
              </span>
              <div className="dd-report__point-copy">
                <strong>
                  {activePoint.title || activePoint.symbol || "Damage point"}
                </strong>
                <p>
                  {formatPointDate(activePoint.date_added) ||
                    "Inspection marker for this lot"}
                </p>
              </div>
              {activePhoto ? (
                <button
                  type="button"
                  className="dd-report__point-thumb"
                  onClick={() => openDamageGallery(activePhoto)}
                >
                  <img src={activePhoto} alt="" />
                </button>
              ) : (
                <span className="dd-report__point-empty">No photo attached</span>
              )}
              <button
                type="button"
                className="dd-report__point-clear"
                onClick={() => {
                  setActiveIndex(-1);
                  setActivePoint(null);
                }}
                aria-label="Clear selected marker"
              >
                <FaTimes />
              </button>
            </div>
          ) : null}
          <div className="dd-report__map">
            <div
              className="dd-report__map-frame"
              onClick={(event) => {
                if (event.target.closest(".dd-report__map-pin")) return;
                setActiveIndex(-1);
                setActivePoint(null);
              }}
            >
              <img
                className="dd-report__map-img"
                src={carDiagram}
                alt="Vehicle damage diagram"
              />
              {points.map((item, index) => {
                const color = markerColor(item.symbol);
                return (
                  <button
                    key={`pin-${index}-${item.symbol || "x"}`}
                    type="button"
                    className={`dd-report__map-pin${
                      activeIndex === index ? " is-active" : ""
                    }`}
                    style={{
                      top: mapOffset(item.top, MAP_HEIGHT),
                      left: mapOffset(item.left, MAP_WIDTH),
                      background: color.bg,
                      "--pin-color": color.bg,
                      "--pin-ring": color.ring,
                    }}
                    title={item.title || item.symbol}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      selectPoint(item, index);
                    }}
                  >
                    {item.symbol || String(index + 1)}
                  </button>
                );
              })}
            </div>
          </div>
          {points.length ? (
            <ul className="dd-report__point-list">
              {points.map((item, index) => (
                <li key={`point-row-${index}`}>
                  <span
                    className="dd-report__legend-swatch"
                    style={{ background: markerColor(item.symbol).bg }}
                  >
                    {item.symbol || index + 1}
                  </span>
                  <strong>{item.title || item.symbol || `Point ${index + 1}`}</strong>
                  <em>{formatPointDate(item.date_added)}</em>
                </li>
              ))}
            </ul>
          ) : null}
          {legendEntries.length ? (
            <div className="dd-report__legend">
              <h4>Damage types</h4>
              <div className="title_list">
                {legendEntries.map((entry) => {
                  const color = markerColor(entry.symbol);
                  const present = entry.count > 0;
                  return (
                    <span
                      key={`legend-${entry.symbol}`}
                      className={`dd-report__legend-item${
                        present ? " is-present" : ""
                      }`}
                    >
                      <span
                        className="dd-report__legend-swatch"
                        style={{ background: color.bg }}
                      >
                        {entry.symbol}
                      </span>
                      <span className="dd-report__legend-copy">
                        <strong>{entry.title}</strong>
                        <em>
                          {present ? `${entry.count} on map` : "Not on this lot"}
                        </em>
                      </span>
                    </span>
                  );
                })}
              </div>
            </div>
          ) : null}
        </section>

      {damagePhotos.length ? (
        <section className="dd-report__card">
          <h3 className="dd-report__card-title">Damage photos</h3>
          <p className="dd-report__card-hint">
            {damagePhotos.length} photo
            {damagePhotos.length === 1 ? "" : "s"} attached to damage markers.
            Click a photo to open it full screen.
          </p>
          <div className="dd-report__photos">
            {damagePhotos.map((photo, index) => (
              <button
                key={`damage-photo-${index}`}
                type="button"
                className="dd-report__photo"
                onClick={() => openGallery(damagePhotos, index)}
              >
                <img src={photo.src} alt="" />
                <span>
                  {[photo.symbol, photo.label].filter(Boolean).join(": ")}
                </span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {sections.length ? (
        <section className="dd-report__card">
          <h3 className="dd-report__card-title">Inspection findings</h3>
          <div className="dd-report__sections">
            {sections.map((item, key) => {
              const open = isSectionOpen(key);
              const questions = Array.isArray(item?.inspection_title)
                ? item.inspection_title
                : [];
              return (
                <div
                  key={item?.id || item?.title || key}
                  className={`dd-report__section${open ? " is-open" : ""}`}
                >
                  <button
                    type="button"
                    className="dd-report__section-head"
                    aria-expanded={open}
                    onClick={() => toggleSection(key)}
                  >
                    <img
                      className="dd-report__section-icon"
                      src={item.photo_url || Car_img2}
                      alt=""
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = Car_img2;
                      }}
                    />
                    <span className="dd-report__section-title">
                      {item.title || `Section ${key + 1}`}
                    </span>
                    <span
                      className={`dd-report__pct dd-report__pct--${
                        scoreTone(item.inspect_count).key
                      }`}
                    >
                      <strong>{`${Number(item.inspect_count) || 0}%`}</strong>
                      <span className="dd-report__pct-track">
                        <i
                          style={{
                            width: `${Number(item.inspect_count) || 0}%`,
                          }}
                        />
                      </span>
                    </span>
                    <span className="dd-report__section-toggle" aria-hidden>
                      {open ? <FaMinus /> : <FaPlus />}
                    </span>
                  </button>
                  {open ? (
                    <div className="dd-report__section-body">
                      {questions.map((entry, entryIndex) => {
                        const selected =
                          entry.selected_inspection_question?.[0] || null;
                        if (!selected) return null;
                        const photos = Array.isArray(selected.images_)
                          ? selected.images_
                          : [];
                        const firstPhoto =
                          photos.map(pickPointPhoto).find(Boolean) || "";
                        const thumb = firstPhoto || Car_img2;
                        return (
                          <div
                            key={entry?.id || entry?.question || entryIndex}
                            className="dd-report__finding"
                          >
                            <img
                              className="dd-report__finding-thumb"
                              src={thumb}
                              alt=""
                              onError={(event) => {
                                event.currentTarget.onerror = null;
                                event.currentTarget.src = Car_img2;
                              }}
                            />
                            <div className="dd-report__finding-copy">
                              <h4>{entry.question}</h4>
                              {firstPhoto ? (
                                <button
                                  type="button"
                                  className="dd-report__finding-link"
                                  onClick={() =>
                                    openGallery(galleryItemsFromPhotos(photos))
                                  }
                                >
                                  <BiImage size={18} /> View image
                                </button>
                              ) : null}
                            </div>
                            <span className="dd-report__finding-answer">
                              {selected.choice_select_ || "—"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      {gallery.open ? (
        <ReportPhotoViewer
          items={gallery.items}
          index={gallery.index}
          onClose={closeGallery}
          onIndexChange={(next) =>
            setGallery((prev) => ({ ...prev, index: next }))
          }
        />
      ) : null}
    </div>
  );
}
